import React from 'react';

import {
  ActionIcon,
  Badge,
  Checkbox,
  Group,
  createStyles,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import WorkoutMovementSelect from 'components/form-fields/select-fields/WorkoutMovementSelect';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import { SCORE_TYPE_OPTIONS, ScoreTypeEnum } from 'types/enums';

import LinkBenchmarkControl from './LinkBenchmarkControl';
import ModalSaveAsBenchmark, { BenchmarkDraft } from './ModalSaveAsBenchmark';
import MovementScoringConfig from './MovementScoringConfig';
import {
  SectionMovement,
  SelectedBenchmark,
  WorkoutSectionData,
  WorkoutSectionFormHandle,
} from '../../../../../forms/workoutBuilder/types';
import ModalBenchmarkForm from '../../../../components/BenchmarkLibrary/components/ModalBenchmarkForm';

interface SectionBodyProps {
  form: WorkoutSectionFormHandle;
  index: number;
  section: WorkoutSectionData;
  onAddMovement: () => void;
  onMovementChange: (
    movementIndex: number,
    movementId: string | null,
    movementName: string | null,
    defaultScoreType: ScoreTypeEnum | null,
  ) => void;
  onMovementScoringChange: (movementIndex: number, updates: Partial<SectionMovement>) => void;
  onDeleteMovement: (movementIndex: number) => void;
  onDisableScoreEntireSection: () => void;
  onEnableSectionAsOneOff: () => void;
  onEnableSectionAsKeptBenchmark: (draft: BenchmarkDraft) => void;
  onRevertToOneOff: () => void;
  onLinkBenchmark: (benchmark: SelectedBenchmark) => void;
  onUnlinkBenchmark: () => void;
  onToggleLevel: (level: 'levelScaled' | 'levelRx' | 'levelRxPlus', enabled: boolean) => void;
  scoreEntireSectionDisabled?: boolean;
}

const useStyles = createStyles(theme => ({
  addMovementButton: {
    border: `2px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    cursor: 'pointer',
    textAlign: 'center',
    backgroundColor: theme.colors.gray[0],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.md,
    '&:hover': { backgroundColor: theme.colors.gray[1] },
  },
  movementRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    columnGap: theme.spacing.md,
    rowGap: 4,
    alignItems: 'end',
  },
  movementCard: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    backgroundColor: theme.white,
  },
}));

const SectionBody = ({
  form,
  index,
  section,
  onAddMovement,
  onMovementChange,
  onMovementScoringChange,
  onDeleteMovement,
  onDisableScoreEntireSection,
  onEnableSectionAsOneOff,
  onEnableSectionAsKeptBenchmark,
  onRevertToOneOff,
  onLinkBenchmark,
  onUnlinkBenchmark,
  onToggleLevel,
  scoreEntireSectionDisabled = false,
}: SectionBodyProps): JSX.Element => {
  const { classes } = useStyles();
  const [benchmarkModalOpened, { open: openBenchmarkModal, close: closeBenchmarkModal }] =
    useDisclosure(false);
  const [createBenchmarkOpened, { open: openCreateBenchmark, close: closeCreateBenchmark }] =
    useDisclosure(false);

  // Section-level mode: the section's score config lives on movements[0]. Bind the Select +
  // MovementScoringConfig to that single entry just like a per-movement row.
  const sectionScoreTypePath = `sections.${index}.movements.0.scoreType`;
  const sectionScoreTypeError = form.getInputProps(sectionScoreTypePath).error as string | undefined;
  const sectionMovement = section.movements[0];

  // Linked only for a Score-Entire-Section kept benchmark with an id anchor (so per-movement
  // sections, whose movements[0] is a real movement, never read as linked).
  const linkedBenchmark =
    section.scoreEntireSection && section.saveAsBenchmark && sectionMovement?.movementId
      ? { id: sectionMovement.movementId, name: sectionMovement.movementName ?? 'Benchmark' }
      : null;

  // Pending "Save as Benchmark": kept, but no id yet — the benchmark is minted at workout-save.
  const isPendingBenchmark =
    section.scoreEntireSection && section.saveAsBenchmark && !sectionMovement?.movementId;
  const pendingBenchmarkName = isPendingBenchmark ? section.name?.trim() || null : null;

  const descriptionEmpty = (section.description ?? '').replace(/<[^>]*>/g, '').trim() === '';
  const descriptionError =
    (form.getInputProps(`sections.${index}.description`).error as string | undefined) ??
    (isPendingBenchmark && descriptionEmpty
      ? 'Add a description — this section will be saved as a benchmark.'
      : undefined);

  const handleScoreEntireSectionToggle = (checked: boolean): void => {
    if (checked) {
      // Defer enabling until the coach chooses in the modal (Save as Benchmark / Just score).
      openBenchmarkModal();
    } else {
      onDisableScoreEntireSection();
    }
  };

  return (
    <Stack spacing='md'>
      <ModalSaveAsBenchmark
        opened={benchmarkModalOpened}
        sectionName={section.name}
        sectionDescription={section.description}
        initialScoreType={sectionMovement?.scoreType ?? null}
        onJustScore={() => {
          onEnableSectionAsOneOff();
          closeBenchmarkModal();
        }}
        onSaveAsBenchmark={draft => {
          onEnableSectionAsKeptBenchmark(draft);
          closeBenchmarkModal();
        }}
        onLinkExisting={benchmark => {
          onLinkBenchmark(benchmark);
          closeBenchmarkModal();
        }}
        onClose={closeBenchmarkModal}
      />
      <ModalBenchmarkForm
        type='create'
        opened={createBenchmarkOpened}
        closeHandler={closeCreateBenchmark}
        onCreated={benchmark => {
          onLinkBenchmark(benchmark);
          closeCreateBenchmark();
        }}
      />
      <TextInput
        label='Section Name'
        placeholder='Enter section name'
        required
        {...form.getInputProps(`sections.${index}.name`)}
        data-cy={`sectionName-${section.id}`}
      />
      <div>
        <Group position='apart' align='center' mb={4}>
          <Text size='sm' weight={500}>
            Describe the Workout
          </Text>
          <LinkBenchmarkControl
            linkedBenchmark={linkedBenchmark}
            pendingBenchmarkName={pendingBenchmarkName}
            onLink={onLinkBenchmark}
            onUnlink={onUnlinkBenchmark}
            onEditPending={openBenchmarkModal}
            onRemovePending={onRevertToOneOff}
            onCreateNew={openCreateBenchmark}
            disabled={scoreEntireSectionDisabled}
            dataCy={`linkBenchmark-${section.id}`}
          />
        </Group>
        <Text size='sm' color='dimmed' mb='xs'>
          Paste or type the workout as you&apos;d write it for athletes.
        </Text>
        <WorkoutRichTextEditor
          value={section.description ?? ''}
          onChange={html => form.setFieldValue(`sections.${index}.description`, html)}
          error={descriptionError}
          dataCy={`sectionDescription-${section.id}`}
        />
      </div>

      {/* A benchmark can only ever be a section's sole movement, so levels can only ever
          be legitimately offered on a Score Entire Section config — never a per-movement
          section (backend: WorkoutLevelsRequireBenchmarkMovementsError). */}
      {section.scoreEntireSection && (
        <Group spacing='md' align='center'>
          <Text size='sm' weight={500}>
            Levels
          </Text>
          <Checkbox
            label='Scaled'
            checked={section.levelScaled}
            onChange={e => onToggleLevel('levelScaled', e.currentTarget.checked)}
            data-cy={`levelScaled-${section.id}`}
            styles={{ label: { paddingLeft: 4 } }}
          />
          <Checkbox
            label='RX'
            checked={section.levelRx}
            onChange={e => onToggleLevel('levelRx', e.currentTarget.checked)}
            data-cy={`levelRx-${section.id}`}
            styles={{ label: { paddingLeft: 4 } }}
          />
          <Checkbox
            label='RX+'
            checked={section.levelRxPlus}
            onChange={e => onToggleLevel('levelRxPlus', e.currentTarget.checked)}
            data-cy={`levelRxPlus-${section.id}`}
            styles={{ label: { paddingLeft: 4 } }}
          />
        </Group>
      )}

      <div>
        <Group position='apart' align='flex-start' mb='md'>
          <div>
            <Title order={5} mb='xs'>
              Scoring & Tracking
            </Title>
            <Text size='sm' color='dimmed'>
              Add movements to track individually, or enable Score Entire Section to score the whole section.
            </Text>
          </div>
          <Group spacing='xs' align='center'>
            <Switch
              label='Score Entire Section'
              checked={section.scoreEntireSection}
              disabled={scoreEntireSectionDisabled}
              onChange={e => handleScoreEntireSectionToggle(e.currentTarget.checked)}
              data-cy={`scoreEntireSection-${section.id}`}
            />
            <Badge color='gray' variant='outline' size='xs'>
              OPTIONAL
            </Badge>
          </Group>
        </Group>

        {section.scoreEntireSection ? (
          <div className={classes.movementCard}>
            <Stack spacing='md'>
              <Select
                label='Scoring Type'
                placeholder='Choose a Scoring Type'
                required
                data={SCORE_TYPE_OPTIONS}
                value={sectionMovement?.scoreType ?? null}
                onChange={value => {
                  const next = (value as ScoreTypeEnum) || null;
                  if (next) form.clearFieldError(sectionScoreTypePath);
                  onMovementScoringChange(0, { scoreType: next });
                }}
                error={sectionScoreTypeError}
                data-cy={`sectionScoringType-${section.id}`}
              />
              {sectionMovement?.scoreType && (
                <MovementScoringConfig
                  movement={sectionMovement}
                  onChange={updates => onMovementScoringChange(0, updates)}
                  dataCyPrefix={`sectionScoring-${section.id}`}
                />
              )}
            </Stack>
          </div>
        ) : (
          <>
            {section.movements.map((movement, movementIndex) => {
              const scoreTypePath = `sections.${index}.movements.${movementIndex}.scoreType`;
              const scoreTypeError = form.getInputProps(scoreTypePath).error as string | undefined;
              const scoreErrorId = `scoringTypeError-${section.id}-${movementIndex}`;
              return (
                <div key={movement.id} className={classes.movementCard}>
                  <Stack spacing='md'>
                    <div className={classes.movementRow}>
                      <WorkoutMovementSelect
                        label='Movement'
                        placeholder='Choose a movement'
                        value={movement.movementId || null}
                        onChange={(value, name, defaultScoreType) =>
                          onMovementChange(movementIndex, value, name, defaultScoreType)
                        }
                        movementName={movement.movementName}
                        data-cy={`movement-${section.id}-${movementIndex}`}
                      />
                      <Select
                        label='Scoring Type'
                        placeholder='Choose a Scoring Type'
                        required
                        data={SCORE_TYPE_OPTIONS}
                        value={movement.scoreType}
                        onChange={value => {
                          const next = (value as ScoreTypeEnum) || null;
                          if (next) form.clearFieldError(scoreTypePath);
                          onMovementScoringChange(movementIndex, { scoreType: next });
                        }}
                        error={!!scoreTypeError}
                        aria-describedby={scoreTypeError ? scoreErrorId : undefined}
                        disabled={!movement.movementId}
                        data-cy={`scoringType-${section.id}-${movementIndex}`}
                      />
                      <ActionIcon
                        variant='subtle'
                        color='red'
                        size='md'
                        onClick={() => onDeleteMovement(movementIndex)}
                        data-cy={`deleteMovement-${section.id}-${movementIndex}`}
                        mb={4}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                      {scoreTypeError && (
                        <Input.Error id={scoreErrorId} sx={{ gridColumnStart: 2 }}>
                          {scoreTypeError}
                        </Input.Error>
                      )}
                    </div>
                    {movement.movementId && movement.scoreType && (
                      <MovementScoringConfig
                        movement={movement}
                        onChange={updates => onMovementScoringChange(movementIndex, updates)}
                        dataCyPrefix={`scoring-${section.id}-${movementIndex}`}
                      />
                    )}
                  </Stack>
                </div>
              );
            })}

            <div
              className={classes.addMovementButton}
              onClick={onAddMovement}
              data-cy={`addMovement-${section.id}`}
            >
              <IconPlus size={20} />
              <Text>Add Tracking</Text>
            </div>
          </>
        )}
      </div>
    </Stack>
  );
};

export default SectionBody;
